import React, { useState, useEffect } from 'react';
import { XMarkIcon, CreditCardIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { subscriptionService } from '../services/subscriptionService';
import toast from 'react-hot-toast';
import { isAndroid } from '../utils/deviceDetection';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  pixCode: string;
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({ 
  isOpen, 
  onClose,
  pixCode
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'pix' | 'card' | null>(null);
  const [loading, setLoading] = useState(false);
  const [pixCodeCopied, setPixCodeCopied] = useState(false);
  const isAndroidDevice = isAndroid();

  // Always show payment method selection - no auto-selection
  useEffect(() => {
    // Reset selection when modal opens to always show options
    if (isOpen) {
      setSelectedMethod(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopyPixCode = () => {
    navigator.clipboard.writeText(pixCode);
    setPixCodeCopied(true);
    toast.success('Código PIX copiado!');
    setTimeout(() => setPixCodeCopied(false), 3000);
  };

  const handlePixPaymentConfirm = async () => {
    try {
      setLoading(true);
      const result = await subscriptionService.confirmPixPayment({ paymentMethod: 'pix' });
      toast.success(result.message || 'Passe de 1 dia ativado com sucesso!');
      
      // Reload the page to refresh subscription status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Error confirming PIX payment:', error);
      toast.error(error.response?.data?.message || 'Erro ao confirmar pagamento PIX');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    try {
      setLoading(true);
      const { url } = await subscriptionService.createOneDayPassCheckout({
        successUrl: `${window.location.origin}${window.location.pathname}?oneDayPass=success`,
        cancelUrl: `${window.location.origin}${window.location.pathname}?oneDayPass=canceled`,
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Falha ao iniciar processo de pagamento');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto border border-gray-200 dark:border-gray-700 shadow-xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              Escolha a Forma de Pagamento
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Passe de 1 Dia - R$ 15,90
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!selectedMethod ? (
          /* Payment Method Selection - Always shown */
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Selecione como deseja pagar pelo seu Passe de 1 Dia:
            </p>

            {/* PIX Option */}
            <button
              onClick={() => setSelectedMethod('pix')}
              className="w-full p-6 border-2 border-green-500 dark:border-green-600 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <QrCodeIcon className="h-10 w-10 text-green-600 dark:text-green-400 mr-4" />
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white">PIX</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Pagamento instantâneo
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">R$ 15,90</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ativação manual</p>
                </div>
              </div>
            </button>

            {/* Card Option */}
            <button
              onClick={() => setSelectedMethod('card')}
              className="w-full p-6 border-2 border-blue-500 dark:border-blue-600 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CreditCardIcon className="h-10 w-10 text-blue-600 dark:text-blue-400 mr-4" />
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white">Cartão de Crédito</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Via Stripe (seguro)
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">R$ 15,90</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ativação automática</p>
                </div>
              </div>
            </button>
          </div>
        ) : selectedMethod === 'pix' ? (
          /* PIX Payment Flow */
          <div className="space-y-6">
            <button
              onClick={() => setSelectedMethod(null)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
            >
              ← Voltar para seleção de pagamento
            </button>

            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-600 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <QrCodeIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                <h4 className="text-xl font-bold text-gray-900 dark:text-white">Pagamento via PIX</h4>
              </div>

              <div className="space-y-4">
                {/* QR Code Display */}
                <div className="flex justify-center">
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border-2 border-green-500 dark:border-green-600">
                    <img 
                      src="/qrcode-pix.png" 
                      alt="QR Code PIX" 
                      className="w-48 h-48 sm:w-64 sm:h-64"
                      onError={(e) => {
                        // Fallback if image is not found
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Escaneie o QR Code com o app do seu banco
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    ou copie o código PIX abaixo
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Código PIX Copia e Cola:
                  </p>
                  <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                    <code className="text-xs break-all text-gray-800 dark:text-gray-200 block mb-2">
                      {pixCode}
                    </code>
                    <button
                      onClick={handleCopyPixCode}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      {pixCodeCopied ? '✓ Copiado!' : 'Copiar Código PIX'}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Como pagar:
                  </p>
                  <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
                    <li>Abra o app do seu banco</li>
                    <li>Escolha pagar com PIX</li>
                    <li>Escaneie o QR Code acima OU cole o código copiado</li>
                    <li>Confirme o pagamento de R$ 15,90</li>
                    <li>Após pagar, clique no botão abaixo</li>
                  </ol>
                </div>

                <div>
                  <button
                    onClick={handlePixPaymentConfirm}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </>
                    ) : (
                      '✓ Pagamento Efetuado'
                    )}
                  </button>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-600 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    <strong>Importante:</strong> Clique em "Pagamento Efetuado" apenas após completar o pagamento no app do seu banco. 
                    O passe será ativado imediatamente ao confirmar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Card Payment Flow */
          <div className="space-y-6">
            <button
              onClick={() => setSelectedMethod(null)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
            >
              ← Voltar para seleção de pagamento
            </button>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 dark:border-blue-600 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CreditCardIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                <h4 className="text-xl font-bold text-gray-900 dark:text-white">Pagamento com Cartão</h4>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Você será redirecionado para a página segura do Stripe para completar o pagamento.
                </p>

                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li>✓ Pagamento seguro via Stripe</li>
                  <li>✓ Ativação automática após confirmação</li>
                  <li>✓ Aceita todos os cartões principais</li>
                </ul>

                <button
                  onClick={handleCardPayment}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Redirecionando...
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="h-5 w-5 mr-2" />
                      Continuar para Pagamento
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodModal;
